// client/views/HelpHintsPanel.tsx
// Admin panel for managing help hints

import React, { useEffect, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/primitives/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import type { HelpHint } from "../ui/help/hints";

export default function HelpHintsPanel() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<HelpHint[]>([]);
  const [locale, setLocale] = useState("en-US");
  const [scope, setScope] = useState<"global"|"org"|"user">("global");
  const [form, setForm] = useState<Partial<HelpHint>>({ locale: "en-US", scope: "global" });

  const fetchAll = async () => {
    try {
      const res = await fetch(`/ui/help/all?q=${encodeURIComponent(q)}`, {
        headers: { "x-role": "admin" }
      });
      if (!res.ok) {
        console.error(`Failed to fetch hints: ${res.status}`);
        return;
      }
      const json = await res.json();
      setItems(json.items || []);
    } catch (err) {
      console.error("Failed to fetch hints:", err);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Initial data fetch only
  }, []);

  async function upsert() {
    if (!form.helpId || !form.text) {
      alert("Please provide helpId and text");
      return;
    }

    const payload = {
      helpId: form.helpId,
      text: form.text,
      locale: form.locale || locale,
      scope,
      orgId: scope === "org" ? form.orgId : undefined,
      userId: scope === "user" ? form.userId : undefined,
    };

    try {
      const res = await fetch("/ui/help", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-role": "admin",
          "x-user": "admin@local"
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setForm({ locale, scope });
        fetchAll();
      } else {
        const err = await res.json();
        alert(`Failed to save: ${JSON.stringify(err)}`);
      }
    } catch (err) {
      console.error("Failed to save hint:", err);
      alert("Failed to save hint");
    }
  }

  async function remove(it: HelpHint) {
    if (!confirm(`Delete hint "${it.helpId}"?`)) return;

    try {
      const res = await fetch("/ui/help", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-role": "admin"
        },
        body: JSON.stringify({
          helpId: it.helpId,
          locale: it.locale,
          scope: it.scope,
          orgId: it.orgId,
          userId: it.userId
        }),
      });

      if (res.ok) {
        fetchAll();
      } else {
        alert("Failed to delete hint");
      }
    } catch (err) {
      console.error("Failed to delete hint:", err);
      alert("Failed to delete hint");
    }
  }

  return (
    <div className="page-container">
      <h1>Help & Hints Management</h1>
      <div className="grid grid-2" style={{marginTop: '2rem'}}>
        <Card>
          <CardHeader><CardTitle>Add / Edit Hint</CardTitle></CardHeader>
          <CardContent style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
            <Input
              placeholder="helpId (e.g., btn.runAnalysis)"
              value={form.helpId || ""}
              onChange={e=>setForm(f=>({...f, helpId:e.target.value}))}
            />
            <Input
              placeholder="Text (≤ 200 chars)"
              value={form.text || ""}
              onChange={e=>setForm(f=>({...f, text:e.target.value}))}
            />
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger><SelectValue placeholder="Locale" /></SelectTrigger>
              <SelectContent><SelectItem value="en-US">en-US</SelectItem></SelectContent>
            </Select>
            <Select value={scope} onValueChange={(v: "global" | "org" | "user")=>setScope(v)}>
              <SelectTrigger><SelectValue placeholder="Scope" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="global">Global</SelectItem>
                <SelectItem value="org">Org</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
            {scope === "org" && (
              <Input
                placeholder="orgId"
                value={form.orgId || ""}
                onChange={e=>setForm(f=>({...f, orgId:e.target.value}))}
              />
            )}
            {scope === "user" && (
              <Input
                placeholder="userId"
                value={form.userId || ""}
                onChange={e=>setForm(f=>({...f, userId:e.target.value}))}
              />
            )}
            <Button helpId="util.helpHints" onClick={upsert} variant="default" className="w-full">
              Save Hint
            </Button>
          </CardContent>
        </Card>

        <Card style={{gridColumn: 'span 2'}}>
          <CardHeader style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
            <CardTitle>Existing Hints</CardTitle>
            <div className="flex flex-gap">
              <Input
                placeholder="Search helpId/text/scope"
                value={q}
                onChange={(e)=>setQ(e.target.value)}
                style={{width: '16rem'}}
              />
              <Button helpId="search.help" variant="outline" onClick={fetchAll}>Search</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
              {items.length === 0 && (
                <div className="empty-state"><p>No hints found. Add one to get started.</p></div>
              )}
              {items.map((it)=>(
                <div
                  key={`${it.scope}:${it.locale}:${it.orgId || ""}:${it.userId || ""}:${it.helpId}`}
                  style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', padding: '0.75rem', background: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--line)'}}
                >
                  <div style={{flex: 1}}>
                    <div style={{fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 600, color: 'var(--secondary)'}}>{it.helpId}</div>
                    <div style={{fontSize: '0.9rem', color: 'var(--text)', marginTop: '0.5rem'}}>{it.text}</div>
                    <div className="text-muted" style={{marginTop: '0.5rem'}}>
                      <span className={`badge badge-${it.scope}`}>{it.scope}</span>
                      {' '}{it.locale}
                      {it.orgId ? ` · org=${it.orgId}` : ""}
                      {it.userId ? ` · user=${it.userId}` : ""}
                    </div>
                  </div>
                  <div className="flex flex-gap" style={{marginLeft: '1rem'}}>
                    <Button helpId="util.editMode" variant="secondary" onClick={()=>setForm(it)}>Edit</Button>
                    <Button helpId="util.helpHints" variant="destructive" onClick={()=>remove(it)}>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
